import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { RHeader, RPage, RViewer } from '../../../components/PdfRender';
import { RSignature } from '../../../components/PdfRender/RSignature';
import { useCompany } from '../../../hooks/company';
import { useAuth } from '../../../hooks/auth';
import { IDentalTreatment, IDentalTreatmentItem } from '../../interfaces';
import { currency } from '../../utils/functions';
import { formatDate } from '../../utils/format-date';
import {
  conditionConfig,
  PaintCondition,
} from '../../../components/odontogram/odontogram.types';
import { styles } from './styles';

interface IDentalTreatmentProcedureReportProps {
  treatment: IDentalTreatment | null;
  item: IDentalTreatmentItem | null;
  signature?: string | null;
  validationCode?: string | null;
  signedAt?: string | null;
  signerName?: string | null;
}

const statusLabel: Record<number, string> = {
  0: 'Pendente',
  1: 'Em andamento',
  2: 'Concluído',
};

const statusStyleMap: Record<
  number,
  'statusPending' | 'statusInProgress' | 'statusDone'
> = {
  0: 'statusPending',
  1: 'statusInProgress',
  2: 'statusDone',
};

export const DentalTreatmentProcedureReport: React.FC<
  IDentalTreatmentProcedureReportProps
> = ({ treatment, item, signature, validationCode, signedAt, signerName }) => {
  const { company } = useCompany();
  const { clinic } = useAuth();

  if (!treatment || !item) return <></>;

  const statusKey = statusStyleMap[item.status] ?? 'statusPending';

  return (
    <RViewer>
      <RPage>
        <View style={styles.container}>
          <RHeader company={company} />

          {/* Cabeçalho */}
          <View style={styles.headerSection}>
            <Text style={styles.headerTitle}>Procedimento Odontológico</Text>
            <View style={styles.dataGrid}>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Nº Tratamento:</Text>
                <Text style={styles.value}>{treatment.code}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Cadastrado:</Text>
                <Text style={styles.value}>
                  {formatDate(item.created_at?.toString())}
                </Text>
              </View>
            </View>
          </View>

          {/* Dados do Paciente */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Dados do Paciente</Text>
            <View style={styles.dataGrid}>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Nome:</Text>
                <Text style={styles.value}>{treatment.client_name || '-'}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>CPF:</Text>
                <Text style={styles.value}>
                  {treatment.client_cpf_cnpj || '-'}
                </Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Celular:</Text>
                <Text style={styles.value}>
                  {treatment.client_cell_phone || '-'}
                </Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Nascimento:</Text>
                <Text style={styles.value}>
                  {formatDate(treatment.client_birthday) || '-'}
                </Text>
              </View>
            </View>
          </View>

          {/* Dados do Procedimento */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Dados do Procedimento</Text>
            <View style={styles.dataGrid}>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Procedimento:</Text>
                <Text style={styles.value}>{item.procedure_name || '-'}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Status:</Text>
                <Text style={[styles.statusBadge, styles[statusKey]]}>
                  {statusLabel[item.status] ?? 'Pendente'}
                </Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Dente:</Text>
                <Text style={styles.value}>{item.tooth_number || '-'}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Face:</Text>
                <Text style={styles.value}>{item.face || '-'}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Estado:</Text>
                <Text style={styles.value}>
                  {conditionConfig[item.current_state as PaintCondition]
                    ?.nome || '-'}
                </Text>
              </View>
              {item.rank != null && (
                <View style={styles.dataRow}>
                  <Text style={styles.label}>Rank:</Text>
                  <Text style={styles.value}>{item.rank}</Text>
                </View>
              )}
              {item.observation ? (
                <View style={styles.dataRowFull}>
                  <Text style={styles.label}>Observação:</Text>
                  <Text style={styles.value}>{item.observation}</Text>
                </View>
              ) : null}
            </View>
          </View>

          {/* Dados Financeiros */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Resumo Financeiro</Text>
            <View style={styles.dataGrid}>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Valor:</Text>
                <Text style={styles.value}>{currency(item.value || 0)}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Desconto:</Text>
                <Text style={styles.value}>{currency(item.discount || 0)}</Text>
              </View>
              <View style={styles.dataRow}>
                <Text style={styles.label}>Total:</Text>
                <Text style={styles.value}>
                  {currency((item.value || 0) - (item.discount || 0))}
                </Text>
              </View>
            </View>
          </View>

          {/* Assinatura */}
          <RSignature
            professional_name={treatment.professional_name}
            signer_name={signerName || treatment.professional_name}
            signature={signature}
            signed_at={signedAt || null}
            validation_code={validationCode || null}
            clinic={clinic}
          />
        </View>
      </RPage>
    </RViewer>
  );
};
